import useConfiguration from '@/composables/configuration/configuration'
import { User } from '@/model/data-object/User'
import { Post } from '@/model/data-object/Post'
import { Topic } from '@/model/data-object/Topic'
import { UserInterface } from '@/model/data-object/UserInterface'
import { OfferLinkPosition, useRefManager } from '@/routing/refManager'

export function useRouter() {
  const { configuration } = useConfiguration()
  const { addRef } = useRefManager()

  function generateCodexRoute() {
    return configuration.envConf.codexUrl
  }

  function generateProfileRoute(user: UserInterface) {
    return user.profileLink
  }

  function generateCentralProfileRote(position: OfferLinkPosition) {
    return addRef(position, configuration.envConf.mySubscriptionUrl)
  }

  function generateBlogRoute(user: User) {
    return user.blog.link
  }

  function generateBuySubscriptionOfferRoute(position: OfferLinkPosition) {
    return addRef(position, configuration.envConf.subscriptionUrl)
  }

  function generateBuyPremiumSubscriptionRoute(position: OfferLinkPosition) {
    return addRef(position, configuration.envConf.premiumSubscriptionUrl)
  }

  function generateFollowersRoute(user: UserInterface) {
    return user.profileLink + '#showFollowers=1'
  }

  function generateSuperLikesRoute(user: UserInterface) {
    return user.profileLink + '#showSuperLike=1'
  }

  function generateAllRepliesRoute(user: UserInterface) {
    return user.profileLink + '#showAllReplies=1'
  }

  function generateMyPostsRoute(user: UserInterface) {
    return user.profileLink + '#showMyPosts=1'
  }

  function generateUnseenRepliesRoute(user: UserInterface) {
    return user.profileLink + '#showUnseenReplies=1'
  }

  function generateShowReportedPosts(user: UserInterface) {
    return user.profileLink + '#showReportedPosts=1'
  }

  function generateSubscribersRoute(user: UserInterface) {
    return user.profileLink + '#showSubscribers=1'
  }

  function generateUserEditRoute() {
    return configuration.envConf.userEditUrl
  }

  function generatePostRoute(post: Post) {
    return post.topic.links.topicLink + '#postId=' + post.id
  }

  function generateFacebookShare(post: Post) {
    return (
      'https://www.facebook.com/sharer/sharer.php?u=' + generatePostRoute(post)
    )
  }

  function generateMessengerShare(post: Post) {
    return 'fb-messenger://share?link=' + generatePostRoute(post)
  }

  function generateWhatsAppShare(post: Post) {
    return 'https://wa.me/?text=' + generatePostRoute(post)
  }

  function generateLinkedinShare(post: Post) {
    return (
      'https://www.linkedin.com/sharing/share-offsite/?url=' +
      generatePostRoute(post)
    )
  }

  function generateViberShare(post: Post) {
    return 'viber://forward?text=' + generatePostRoute(post)
  }

  function generatePostRespondRoute(post: Post) {
    return post.topic.links.topicLink + '#respondToPostId=' + post.id
  }

  function generateTopicRoute(topic: Topic) {
    return topic.links.topicLink
  }

  function generateOriginRoute(topic: Topic) {
    return topic.links.articleLink
  }

  function generateLoginRoute(topic: Topic) {
    if (configuration.widget.loginUrl) {
      return configuration.widget.loginUrl
    }
    const params = [
      'url=' +
        encodeURIComponent(
          configuration.widget.currentUrl ?? topic.links.topicLink
        ),
    ]
    if (configuration.widget.darkModeEnabled) {
      params.push(configuration.envConf.loginDarkModeParam)
    }

    return configuration.envConf.loginUrl + '?' + params.join('&')
  }

  function generateSwitchAccountRoute(topic: Topic) {
    const params = [
      'url=' +
        encodeURIComponent(
          configuration.widget.currentUrl ?? topic.links.topicLink
        ),
    ]
    if (configuration.widget.darkModeEnabled) {
      params.push(configuration.envConf.loginDarkModeParam)
    }

    return configuration.envConf.loginUrl + '?' + params.join('&')
  }

  return {
    generateShowReportedPosts,
    generateCodexRoute,
    generateProfileRoute,
    generateFollowersRoute,
    generatePostRoute,
    generatePostRespondRoute,
    generateSwitchAccountRoute,
    generateSubscribersRoute,
    generateLoginRoute,
    generateTopicRoute,
    generateBlogRoute,
    generateOriginRoute,
    generateUserEditRoute,
    generateUnseenRepliesRoute,
    generateAllRepliesRoute,
    generateFacebookShare,
    generateMessengerShare,
    generateWhatsAppShare,
    generateLinkedinShare,
    generateViberShare,
    generateMyPostsRoute,
    generateBuySubscriptionOfferRoute,
    generateCentralProfileRote,
    generateSuperLikesRoute,
    generateBuyPremiumSubscriptionRoute,
  }
}
